/**
 * Sticky CTA bar island.
 *
 * Shows a fixed bottom bar with shop links when the hero's effective cost
 * scrolls out of the viewport. Hides when the full ShopLinks section
 * scrolls into view to avoid duplication.
 *
 * Mobile only (≤640px). Respects prefers-reduced-motion.
 */
export function initStickyCta(): void {
  const bar = document.querySelector<HTMLElement>('[data-sticky-cta]');
  if (!bar) return;

  const trigger = document.querySelector<HTMLElement>('[data-sticky-cta-trigger]');
  const target = document.querySelector<HTMLElement>('[data-sticky-cta-target]');
  if (!trigger || !target) return;

  const syncCondition = () => {
    const selected = target.querySelector<HTMLInputElement>('input[data-condition]:checked');
    if (!selected) return;
    for (const panel of bar.querySelectorAll<HTMLElement>('[data-sticky-condition]')) {
      panel.hidden = panel.dataset.stickyCondition !== selected.dataset.condition;
    }
  };
  target.addEventListener('change', syncCondition);
  syncCondition();

  const MQ = window.matchMedia('(max-width: 640px)');
  let active = MQ.matches;
  let triggerOut = false;
  let targetIn = false;

  const update = () => {
    if (active && triggerOut && !targetIn) {
      bar.dataset.visible = '';
    } else {
      delete bar.dataset.visible;
    }
  };

  // Show bar when trigger scrolls out; hide when target scrolls in
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.target === trigger) {
          triggerOut = !entry.isIntersecting;
        } else if (entry.target === target) {
          targetIn = entry.isIntersecting;
        }
      }
      update();
    },
    { threshold: 0 },
  );
  observer.observe(trigger);
  observer.observe(target);

  // React to viewport changes (rotate, desktop ↔ mobile)
  MQ.addEventListener('change', (e) => {
    active = e.matches;
    update();
  });

  // Reset on page navigation (BFCache / View Transitions)
  const reset = () => {
    syncCondition();
    triggerOut = false;
    targetIn = false;
    update();
  };
  window.addEventListener('pageshow', reset);
  window.addEventListener('pagereveal' as string, reset);
}
